import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, FileText } from "lucide-react";
import { api } from "@/lib/api";
import "@/styles/print.css";

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
};

const formatChicagoCitation = (citation, index) => {
  const parts = [];
  
  if (citation.author) parts.push(citation.author);
  if (citation.title) parts.push(`"${citation.title}"`);
  if (citation.publisher) parts.push(citation.publisher);
  if (citation.date_published) parts.push(citation.date_published);
  if (citation.page_or_timecode) parts.push(`p. ${citation.page_or_timecode}`);
  
  return (
    <div key={citation.id} className="print-footnote">
      <span className="print-footnote-num">{index + 1}.</span>{" "}
      {parts.join(", ") || citation.title || "Untitled source"}.
      {citation.url && ` Available at: ${citation.url}`}
    </div>
  );
};

const PrintFactsPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [fact, setFact] = useState(null);
  const [citations, setCitations] = useState([]);
  const [relatedClaims, setRelatedClaims] = useState([]);
  const [correctionsCount, setCorrectionsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasPrinted = useRef(false);
  const autoPrint = searchParams.get("auto") === "true";

  const loadData = useCallback(async () => {
    try {
      // Load fact correction
      const factRes = await api.get(`/facts/${slug}`);
      setFact(factRes.data);
      
      // Load all citations to find related ones
      const citRes = await api.get("/citations");
      const allCitations = citRes.data;
      
      // Filter citations linked to this fact's claims
      const factClaimIds = factRes.data.claim_ids || [];
      const relevantCitations = allCitations.filter(c => 
        factClaimIds.includes(c.claim_id)
      );
      setCitations(relevantCitations);
      
      // Load related claims
      if (factClaimIds.length > 0) {
        const claimsRes = await api.get("/claims");
        const related = claimsRes.data.filter(c => factClaimIds.includes(c.id));
        setRelatedClaims(related);
      }
      
      // Get corrections count
      try {
        const logRes = await api.get(`/corrections-log?item_type=fact_correction&item_id=${factRes.data.id}`);
        setCorrectionsCount(logRes.data.length);
      } catch {
        setCorrectionsCount(0);
      }
      
    } catch (err) {
      setError("Failed to load fact correction");
    }
    setLoading(false);
  }, [slug]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    // Auto-print after content loads
    if (!loading && fact && autoPrint && !hasPrinted.current) {
      hasPrinted.current = true;
      setTimeout(() => window.print(), 500);
    }
  }, [loading, fact, autoPrint]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="print-layout">
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  if (error || !fact) {
    return (
      <div className="print-layout">
        <div className="text-center py-20 text-red-600">{error || "Fact correction not found"}</div>
      </div>
    );
  }

  const printDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });

  const sourceUrl = `${window.location.origin}/facts/${slug}`;

  return (
    <div className="print-layout" data-testid="print-facts-page">
      {/* Print Controls (hidden in print) */}
      <div className="print-controls no-print">
        <div className="flex items-center gap-4">
          <Link to={`/facts/${slug}`} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4" />
            Back to Site
          </Link>
          <span className="text-gray-400">|</span>
          <span className="text-sm text-gray-600">
            <FileText className="w-4 h-4 inline mr-1" />
            Fact Correction Document
          </span>
        </div>
        <button onClick={handlePrint} data-testid="print-btn">
          <Printer className="w-4 h-4 inline mr-2" />
          Print Document
        </button>
      </div>
      
      {/* Print Header */}
      <div className="print-header">
        <h1>Elvis Aloha 1973 Historical Archive</h1>
        <div className="print-meta">
          Fact Correction Document
          <br />
          Printed: {printDate} | Source: {sourceUrl}
        </div>
      </div>
      
      {/* Document Content */}
      <div className="print-section-header">
        Fact Correction
      </div>
      
      {/* Claim Summary */}
      <div className="print-fact-section print-no-break">
        <div className="print-fact-label">Claim</div>
        <div className="print-entry-title" style={{ marginTop: 0 }}>
          {fact.claim_summary}
        </div>
      </div>
      
      {/* Confidence Level */}
      <div className="print-fact-section">
        <div className="print-fact-label">Confidence Level</div>
        <div className="print-confidence">
          {fact.confidence_level || "Not Specified"}
        </div>
      </div>
      
      {/* Origin */}
      {fact.origin && (
        <div className="print-fact-section print-no-break">
          <div className="print-fact-label">Origin</div>
          <div className="print-fact-content print-narrative">
            {fact.origin.split("\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      )}
      
      {/* Spread Mechanism */}
      {fact.spread_mechanism && (
        <div className="print-fact-section print-no-break">
          <div className="print-fact-label">Spread Mechanism</div>
          <div className="print-fact-content print-narrative">
            {fact.spread_mechanism.split("\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      )}
      
      {/* Evidence Review */}
      {fact.evidence_review && (
        <div className="print-fact-section print-no-break">
          <div className="print-fact-label">Evidence Review</div>
          <div className="print-fact-content print-narrative">
            {fact.evidence_review.split("\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      )}
      
      {/* Evidence Consulted (Citations) */}
      {citations.length > 0 && (
        <div className="print-footnotes print-no-break">
          <div className="print-footnotes-title">Evidence Consulted</div>
          {citations.map((citation, i) => formatChicagoCitation(citation, i))}
        </div>
      )}
      
      {/* Related Claims */}
      {relatedClaims.length > 0 && (
        <div className="print-related print-no-break">
          <div className="print-related-title">Related Claims</div>
          <ul className="print-related-list">
            {relatedClaims.map(claim => (
              <li key={claim.id}>
                <strong>[{claim.id.substring(0, 8)}]</strong>:{" "}
                {claim.claim_text?.length > 100 
                  ? claim.claim_text.substring(0, 100) + "..."
                  : claim.claim_text}
                {claim.claim_status && ` (${claim.claim_status})`}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Revision History Summary */}
      <div className="print-revision">
        <strong>Revision History</strong>
        <br />
        Created: {formatDate(fact.created_at)}
        <br />
        Last Updated: {formatDate(fact.updated_at || fact.created_at)}
        <br />
        Total Revisions: {correctionsCount}
      </div>
      
      {/* Print Footer (shown in print only via CSS) */}
      <div className="print-footer">
        Elvis Aloha 1973 Historical Archive | {sourceUrl}
      </div>
    </div>
  );
};

export default PrintFactsPage;
